import { getBindMedia, checkMedia, checkCookie } from '@/request/mediaPlatform';
import { ref } from 'vue';
export default function useProcessMediaAccounts() {
  const status = ref<any>(null);
  const errorMessage = ref<string | null>(null);

  // 返回一个Promise，以便于外部调用时可以使用.then/.catch处理结果
  const processAndCheckMediaAccountsPromise = () => {
    return new Promise(async (resolve, reject) => {
      try {
        const mediaResponse = await getBindMedia();

        if (!mediaResponse.data.data.length) {
          throw new Error('请先绑定多媒体账号');
        }

        if (mediaResponse.data.code !== 200) {
          throw new Error(`获取多媒体账号失败，错误码: ${mediaResponse.data.code}`);
        }

        const mediaIds = mediaResponse.data.data.map((account) => account.id);

        if (!mediaIds.length) {
          throw new Error('当前未绑定任何多媒体账号');
        }

        const checkResults = await Promise.all(
          mediaIds.map(async (accountId) => {
            try {
              const res = await checkCookie({ account: accountId });
              return { account: accountId, code: res.data.code };
            } catch (error) {
              return { account: accountId, error };
            }
          })
        );

        const failedAccounts = checkResults
          .filter((result) => result.code === 500 || (result.error && result.error instanceof Error))
          .map((result) => result.account);

        if (failedAccounts.length) {
          throw new Error(failedAccounts.map((account) => `${account}账号过期请重新授权绑定`).join('，'));
        }

        status.value = { msg: '所有多媒体账号检查通过，准备发布', mediaIds };
        resolve(status.value); // 成功时调用resolve
      } catch (err: unknown) {
        if (err instanceof Error) {
          errorMessage.value = err.message;
          reject(err);
        } else {
          // 这里处理非 Error 类型的异常情况
          reject(new Error('An unexpected error occurred'));
        }
      }
    });
  };

  return {
    processAndCheckMediaAccountsPromise,
    // 可选：如果组件内需要直接访问到status和errorMessage的实时状态
    getStatus: () => status.value,
    getErrorMessage: () => errorMessage.value,
  };
}

import { generateVideoTDK } from '@/request/aiAction';

export function useGenerateTDKData() {
  const tdkData = ref({
    title: '',
    context: '',
    tag: '',
    contextAndText: '',
  });
  const tagOptions = ref([]);

  const generateTDKData = async (inputs = {}) => {
    const requestBody = {
      inputs,
      response_mode: 'streaming',
      user: 'abc-123',
    };

    try {
      const response = await generateVideoTDK(requestBody);
      if (!response.body) return { tdkData: tdkData.value, tagOptions: tagOptions.value };
      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();

      let firstResultReceived = false;
      let result = '';
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += value;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const cleanedValue = line.replace(/^data: /, '').trim();
          if (cleanedValue) {
            try {
              const json = JSON.parse(cleanedValue);
              if (json.data && json.data.text) {
                if (!firstResultReceived) {
                  result = '';
                  firstResultReceived = true;
                }
                result += json.data.text;
              }
            } catch (e) {
              console.error('Error parsing JSON:', e);
            }
          }
        }
      }

      const cleanedResult = result.replace(/```json|```/g, '').trim();
      let jsonResult = JSON.parse(cleanedResult);

      tagOptions.value = jsonResult.tag.split(' ').map((tag) => ({
        value: tag.slice(1),
        label: tag.slice(1),
      }));
      tdkData.value = {
        ...jsonResult,
        contextAndText: jsonResult.context + jsonResult.tag,
        tagArr: tagOptions.value?.map((tag: any) => tag?.value) || [],
      };

      return { tdkData: tdkData.value, tagOptions: tagOptions.value };
    } catch (error) {
      console.error('Error fetching prompt generate:', error);
      return { tdkData: tdkData.value, tagOptions: tagOptions.value };
    }
  };

  return {
    generateTDKData,
    getTDKData: () => tdkData.value,
    getTagOptions: () => tagOptions.value,
  };
}
